import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../Supabase";
import { PRIVACY_URL, TERMS_URL, TERMS_VERSION } from "../lib/legal";
import { getResetPasswordRedirect } from "../lib/authRedirects";
import { INPUT_LIMITS, clampFields, clampText } from "../lib/inputLimits";
import {
  STRONG_PASSWORD_RULES,
  meetsStrongPasswordPolicy,
} from "../lib/passwordPolicy";

const limit = (value, key) => clampText(value ?? "", INPUT_LIMITS[key] ?? 255);

const AuthScreen = () => {
  const [mode, setMode] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const [appleLoading, setAppleLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postCode, setPostCode] = useState("");
  const [payee, setPayee] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [hasLoggedInBefore, setHasLoggedInBefore] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("hasLoggedInBefore") === "true";
    } catch (e) {
      return false;
    }
  });

  const emailOk = useMemo(() => /\S+@\S+\.\S+/.test(email), [email]);
  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  const passwordOk = useMemo(() => {
    if (mode === "signin") {
      return password.length >= 6;
    }
    return meetsStrongPasswordPolicy(password);
  }, [mode, password]);

  const canSubmit = useMemo(() => {
    if (mode === "signin") {
      return (
        emailOk && passwordOk && !loading && !appleLoading && !googleLoading
      );
    }

    const extraOk =
      companyName.trim() &&
      address.trim() &&
      city.trim() &&
      postCode.trim() &&
      payee.trim() &&
      sortCode.trim() &&
      accountNumber.trim();

    return (
      emailOk &&
      passwordOk &&
      extraOk &&
      termsAccepted &&
      !emailExists &&
      !checkingEmail &&
      !loading &&
      !appleLoading &&
      !googleLoading
    );
  }, [
    emailOk,
    mode,
    companyName,
    address,
    city,
    postCode,
    payee,
    sortCode,
    accountNumber,
    loading,
    appleLoading,
    googleLoading,
    passwordOk,
    termsAccepted,
    emailExists,
    checkingEmail,
  ]);

  const checkEmailExists = useCallback(async (targetEmail) => {
    const { data, error } = await supabase.functions.invoke(
      "check-email-exists",
      {
        body: { email: targetEmail },
      }
    );
    if (error) throw error;
    return Boolean(data?.exists);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const errorCode = params.get("error_code");
    const error = params.get("error");

    if (
      errorCode === "otp_expired" ||
      errorCode === "otp_disabled" ||
      error === "access_denied"
    ) {
      setErr(
        "Your reset link is invalid or has expired. Please request a new password reset email."
      );
    }
  }, []);

  useEffect(() => {
    if (mode !== "signup") {
      setEmailExists(false);
      setCheckingEmail(false);
      return;
    }

    if (!emailOk) {
      setEmailExists(false);
      setCheckingEmail(false);
      return;
    }

    let isCurrent = true;
    setCheckingEmail(true);

    const timer = setTimeout(async () => {
      try {
        const exists = await checkEmailExists(normalizedEmail);
        if (!isCurrent) return;
        setEmailExists(exists);
      } catch (e) {
        if (!isCurrent) return;
        console.warn("Email uniqueness check failed", e);
        setEmailExists(false);
      } finally {
        if (isCurrent) setCheckingEmail(false);
      }
    }, 300);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [mode, emailOk, normalizedEmail, checkEmailExists]);

  const storeProfileDraft = (addr, payload) => {
    if (typeof window === "undefined" || !addr) return;
    try {
      window.localStorage.setItem(
        `profileDraft:${addr.toLowerCase()}`,
        JSON.stringify(payload)
      );
    } catch (e) {
      console.warn("Unable to store profile draft", e);
    }
  };

  const consumeProfileDraft = (addr) => {
    if (typeof window === "undefined" || !addr) return null;
    try {
      const key = `profileDraft:${addr.toLowerCase()}`;
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      window.localStorage.removeItem(key);
      return JSON.parse(raw);
    } catch (e) {
      console.warn("Unable to consume profile draft", e);
      return null;
    }
  };

  const ensureProfileExists = async (user) => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from("profiles")
      .select("id")
      .eq("id", user.id)
      .maybeSingle();

    if (error) throw error;
    if (data?.id) return;

    const draft = consumeProfileDraft(user.email) || user?.user_metadata || {};

    const termsAcceptedAt =
      draft?.terms_accepted_at ||
      (draft?.terms_accepted ? new Date().toISOString() : null);

    const profilePayload = clampFields(
      {
        id: user.id,
        email: (draft.email || user.email || "").trim(),
        company_name: (draft.company_name || "").trim(),
        address: (draft.address || "").trim(),
        city: (draft.city || "").trim(),
        post_code: (draft.post_code || "").trim(),
        payee: (draft.payee || "").trim(),
        sort_code: (draft.sort_code || "").trim(),
        account_number: (draft.account_number || "").trim(),
        terms_accepted: Boolean(draft?.terms_accepted),
        terms_accepted_at: termsAcceptedAt,
        terms_version: draft?.terms_version
          ? String(draft.terms_version).trim()
          : "",
      },
      {
        email: INPUT_LIMITS.email,
        company_name: INPUT_LIMITS.companyName,
        address: INPUT_LIMITS.address,
        city: INPUT_LIMITS.city,
        post_code: INPUT_LIMITS.postCode,
        payee: INPUT_LIMITS.payee,
        sort_code: INPUT_LIMITS.sortCode,
        account_number: INPUT_LIMITS.accountNumber,
        terms_version: INPUT_LIMITS.termsVersion,
      }
    );

    const { error: insertError } = await supabase
      .from("profiles")
      .insert(profilePayload);

    if (insertError) throw insertError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setLoading(true);
      setErr(null);
      setInfo(null);

      if (mode === "signin") {
        const { data: sessionData, error } =
          await supabase.auth.signInWithPassword({
            email: normalizedEmail,
            password,
          });
        if (error) {
          if (
            error.code === "email_not_confirmed" ||
            /email.*confirm/i.test(error.message || "")
          ) {
            throw new Error(
              "Please confirm your email address before signing in."
            );
          }
          throw error;
        }

        const userId = sessionData?.user?.id;
        if (userId) {
          await ensureProfileExists(sessionData.user);
          try {
            window.localStorage.setItem("hasLoggedInBefore", "true");
          } catch (storageErr) {
            console.warn("Unable to persist login marker", storageErr);
          }
          setHasLoggedInBefore(true);
        }
      } else {
        const termsAcceptedAt = new Date().toISOString();

        try {
          const exists = await checkEmailExists(normalizedEmail);
          if (exists) {
            setEmailExists(true);
            setMode("signin");
            throw new Error(
              "An account already exists with this email. Please sign in or reset your password."
            );
          }
        } catch (lookupErr) {
          if (lookupErr?.message) {
            throw lookupErr;
          }
          console.warn("Email uniqueness check failed, continuing", lookupErr);
        }

        const profileDraft = clampFields(
          {
            email: normalizedEmail,
            company_name: companyName.trim(),
            address: address.trim(),
            city: city.trim(),
            post_code: postCode.trim(),
            payee: payee.trim(),
            sort_code: sortCode.trim(),
            account_number: accountNumber.trim(),
            terms_accepted: true,
            terms_accepted_at: termsAcceptedAt,
            terms_version: TERMS_VERSION,
          },
          {
            email: INPUT_LIMITS.email,
            company_name: INPUT_LIMITS.companyName,
            address: INPUT_LIMITS.address,
            city: INPUT_LIMITS.city,
            post_code: INPUT_LIMITS.postCode,
            payee: INPUT_LIMITS.payee,
            sort_code: INPUT_LIMITS.sortCode,
            account_number: INPUT_LIMITS.accountNumber,
            terms_version: INPUT_LIMITS.termsVersion,
          }
        );

        const { error: signUpError } = await supabase.auth.signUp({
          email: normalizedEmail,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: profileDraft,
          },
        });
        if (signUpError) {
          const messageText = (signUpError.message || "").toLowerCase();
          const duplicateEmail =
            signUpError.code === "user_already_registered" ||
            signUpError.code === "user_already_exists" ||
            /already\s+(registered|exists?)/.test(messageText);

          if (duplicateEmail) {
            setMode("signin");
            throw new Error(
              "An account already exists with this email. Please sign in or reset your password."
            );
          }

          throw signUpError;
        }

        storeProfileDraft(normalizedEmail, profileDraft);
        setInfo(
          "Check your email to confirm your account. You can sign in once it is verified."
        );
        setMode("signin");
      }
    } catch (e) {
      console.warn("Auth error", e);
      setErr(e?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || resendLoading || loading || appleLoading || googleLoading) {
      return;
    }

    try {
      setErr(null);
      setInfo(null);
      setResendLoading(true);

      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      setInfo("Confirmation email resent. Please check your inbox.");
    } catch (e) {
      console.warn("Resend confirmation failed", e);
      setErr(e?.message || "Could not resend confirmation email.");
    } finally {
      setResendLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (
      mode !== "signin" ||
      resetLoading ||
      loading ||
      appleLoading ||
      googleLoading
    ) {
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setErr("Enter a valid email to receive a reset link.");
      return;
    }

    try {
      setErr(null);
      setInfo(null);
      setResetLoading(true);

      const redirectTo = getResetPasswordRedirect();

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (error) throw error;

      setInfo("Password reset email sent. Please check your inbox.");
    } catch (e) {
      console.warn("Password reset request failed", e);
      setErr(e?.message || "Could not send password reset email.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    if (loading || appleLoading || googleLoading) return;

    try {
      setErr(null);
      setAppleLoading(true);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
      });

      if (error) throw error;
      // On success, user will be redirected by Supabase
    } catch (e) {
      console.warn("Apple sign-in error", e);
      setErr(e?.message || "Apple sign-in failed. Please try again.");
    } finally {
      setAppleLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (loading || appleLoading || googleLoading) return;

    try {
      setErr(null);
      setGoogleLoading(true);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          // Change this to your dedicated callback route if you have one
          redirectTo: window.location.origin,
        },
      });

      if (error) throw error;
      // On success, user will be redirected by Supabase
    } catch (e) {
      console.warn("Google sign-in error", e);
      setErr(e?.message || "Google sign-in failed. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-black text-sm font-semibold text-white">
            IU
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold text-slate-900">InvoiceU</h1>
            <p className="text-xs text-slate-500">
              Sign in to your invoicing dashboard
            </p>
          </div>
        </div>

        <div className="mt-5 flex rounded-full bg-slate-100 p-1 text-xs font-medium">
          <button
            type="button"
            onClick={() => {
              setMode("signin");
              setErr(null);
              setInfo(null);
            }}
            className={[
              "flex-1 rounded-full px-3 py-1.5",
              mode === "signin" ? "bg-black text-white" : "text-slate-600",
            ].join(" ")}
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("signup");
              setErr(null);
              setInfo(null);
            }}
            className={[
              "flex-1 rounded-full px-3 py-1.5",
              mode === "signup" ? "bg-black text-white" : "text-slate-600",
            ].join(" ")}
          >
            Sign up
          </button>
        </div>

        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:bg-white focus:ring-1 focus:ring-slate-500"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(limit(e.target.value, "email"))}
              maxLength={INPUT_LIMITS.email}
            />
            {mode === "signup" && checkingEmail && emailOk && (
              <p className="mt-1 text-[11px] text-slate-500">
                Checking if this email is available…
              </p>
            )}
            {mode === "signup" && emailExists && (
              <p className="mt-1 text-[11px] text-amber-700">
                An account already exists with this email. Please sign in or
                reset your password.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:bg-white focus:ring-1 focus:ring-slate-500"
              placeholder={
                mode === "signup"
                  ? "12+ chars incl. symbol"
                  : "Minimum 6 characters"
              }
              value={password}
              onChange={(e) => setPassword(limit(e.target.value, "password"))}
              maxLength={INPUT_LIMITS.password}
            />
            {mode === "signup" && (
              <ul className="mt-1 space-y-0.5 text-[11px]">
                {STRONG_PASSWORD_RULES.map((rule) => {
                  const valid = rule.test(password);
                  return (
                    <li
                      key={rule.label}
                      className={valid ? "text-emerald-600" : "text-slate-400"}
                    >
                      {valid ? "✓" : "•"} {rule.label}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {mode === "signin" && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handlePasswordReset}
                disabled={
                  resetLoading ||
                  loading ||
                  appleLoading ||
                  googleLoading ||
                  !/\S+@\S+\.\S+/.test(email)
                }
                className={[
                  "text-[11px] font-semibold underline",
                  resetLoading ||
                  loading ||
                  appleLoading ||
                  googleLoading ||
                  !/\S+@\S+\.\S+/.test(email)
                    ? "cursor-not-allowed text-slate-400"
                    : "text-slate-700 hover:text-slate-900",
                ].join(" ")}
              >
                {resetLoading ? "Sending reset email…" : "Forgot password?"}
              </button>
            </div>
          )}

          {mode === "signup" && (
            <>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <MiniInput
                  label="Company name"
                  value={companyName}
                  onChange={(v) => setCompanyName(limit(v, "companyName"))}
                  maxLength={INPUT_LIMITS.companyName}
                />
                <MiniInput
                  label="Address"
                  value={address}
                  onChange={(v) => setAddress(limit(v, "address"))}
                  maxLength={INPUT_LIMITS.address}
                />
                <MiniInput
                  label="City"
                  value={city}
                  onChange={(v) => setCity(limit(v, "city"))}
                  maxLength={INPUT_LIMITS.city}
                />
                <MiniInput
                  label="Post code"
                  value={postCode}
                  onChange={(v) => setPostCode(limit(v, "postCode"))}
                  maxLength={INPUT_LIMITS.postCode}
                />
              </div>

              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <MiniInput
                  label="Payee"
                  value={payee}
                  onChange={(v) => setPayee(limit(v, "payee"))}
                  maxLength={INPUT_LIMITS.payee}
                />
                <MiniInput
                  label="Sort code"
                  value={sortCode}
                  onChange={(v) => setSortCode(limit(v, "sortCode"))}
                  placeholder="12-34-56"
                  maxLength={INPUT_LIMITS.sortCode}
                />
                <MiniInput
                  label="Account number"
                  value={accountNumber}
                  onChange={(v) => setAccountNumber(limit(v, "accountNumber"))}
                  placeholder="12345678"
                  maxLength={INPUT_LIMITS.accountNumber}
                />
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-700">
                <input
                  id="terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-black focus:ring-black"
                />
                <label htmlFor="terms" className="leading-snug">
                  I agree to the{" "}
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-slate-900 underline"
                  >
                    Terms & Conditions
                  </a>{" "}
                  (version {TERMS_VERSION}) and acknowledge the{" "}
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-slate-900 underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
            </>
          )}

          {err && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {err}
            </div>
          )}

          {info && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className={[
              "mt-2 flex w-full items-center justify-center rounded-full px-4 py-2 text-sm font-semibold",
              canSubmit
                ? "bg-black text-white hover:bg-slate-800"
                : "cursor-not-allowed bg-slate-200 text-slate-500",
            ].join(" ")}
          >
            {loading
              ? "Please wait…"
              : mode === "signin"
              ? "Sign in"
              : "Create account"}
          </button>

          {mode === "signin" && !hasLoggedInBefore && (
            <button
              type="button"
              onClick={handleResend}
              disabled={
                resendLoading ||
                loading ||
                appleLoading ||
                googleLoading ||
                !/\S+@\S+\.\S+/.test(email)
              }
              className={[
                "flex w-full items-center justify-center rounded-full border px-4 py-2 text-xs font-semibold",
                resendLoading ||
                loading ||
                appleLoading ||
                googleLoading ||
                !/\S+@\S+\.\S+/.test(email)
                  ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              {resendLoading ? "Resending…" : "Resend confirmation email"}
            </button>
          )}
        </form>

        {/* OR divider */}
        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-[11px] font-medium text-slate-400">or</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        {/* Social login buttons */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleAppleLogin}
            disabled={loading || appleLoading || googleLoading}
            className={[
              "flex w-full items-center justify-center rounded-full px-4 py-2 text-sm font-semibold",
              "border border-black bg-black text-white hover:bg-slate-900",
              (loading || appleLoading || googleLoading) &&
                "cursor-not-allowed opacity-70",
            ].join(" ")}
          >
            {appleLoading ? "Connecting…" : "Continue with Apple"}
          </button>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading || appleLoading || googleLoading}
            className={[
              "flex w-full items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50",
              (loading || appleLoading || googleLoading) &&
                "cursor-not-allowed opacity-70",
            ].join(" ")}
          >
            {googleLoading ? (
              "Connecting…"
            ) : (
              <span className="flex items-center gap-2">
                <GoogleIcon className="h-4 w-4" />
                <span>Continue with Google</span>
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const MiniInput = ({ label, value, onChange, placeholder, maxLength }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] font-medium text-slate-700">{label}</label>
    <input
      type="text"
      className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs outline-none focus:border-slate-500 focus:bg-white focus:ring-1 focus:ring-slate-500"
      placeholder={placeholder || label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength}
    />
  </div>
);

const GoogleIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 18 18"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="#4285F4"
      d="M17.64 9.2045c0-.6385-.0573-1.2518-.1636-1.8364H9v3.4727h4.8445a4.1435 4.1435 0 0 1-1.7973 2.7168v2.256h2.9078c1.7031-1.5684 2.685-3.881 2.685-6.6091z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.466-.806 5.955-2.186l-2.908-2.256c-.806.54-1.837.863-3.047.863-2.344 0-4.329-1.58-5.035-3.708H.957v2.332C2.438 15.982 5.481 18 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.965 10.713a5.41 5.41 0 0 1 0-3.426V4.955H.957a9 9 0 0 0 0 8.09l3.008-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.579c1.32 0 2.508.454 3.441 1.346l2.582-2.582C13.463.848 11.427 0 9 0 5.481 0 2.438 2.018.957 4.955l3.008 2.332C4.671 5.159 6.656 3.579 9 3.579z"
    />
  </svg>
);

export default AuthScreen;
